import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, RotateCcw, History, Save, X } from "lucide-react";
import { api } from "@/lib/auth";

const STATUSES = ["draft", "scheduled", "active", "inactive"];

const emptyTier = () => ({
    label: "",
    min_plays: 200,
    max_plays: null,
    price_per_play_cents: 50,
    status: "active",
    starts_at: "",
    ends_at: "",
    display_order: 0,
    audience: "public",
    restricted_user_ids: [],
});

const centsToStr = (c) => (c == null ? "—" : `$${(c / 100).toFixed(2)}`);

export const PricingTiersTab = ({ withElevation }) => {
    const [tiers, setTiers] = useState(null);
    const [config, setConfig] = useState(null);
    const [history, setHistory] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [minEdit, setMinEdit] = useState("");

    const reload = () => {
        api.get("/pricing/tiers").then(({ data }) => setTiers(data.tiers || []))
            .catch(() => setTiers([]));
        api.get("/pricing/config").then(({ data }) => {
            setConfig(data);
            setMinEdit(String(data.min_purchase || 200));
        }).catch(() => setConfig({ min_purchase: 200 }));
    };

    useEffect(() => { reload(); }, []);

    const validationHints = useMemo(() => {
        if (!tiers) return [];
        const activePub = tiers.filter(t => t.status === "active" && t.audience === "public")
            .sort((a, b) => a.min_plays - b.min_plays);
        const issues = [];
        // gap detection between adjacent tiers
        for (let i = 0; i < activePub.length - 1; i++) {
            const cur = activePub[i], nxt = activePub[i + 1];
            if (cur.max_plays == null) continue;
            if (nxt.min_plays > cur.max_plays + 1) {
                issues.push(`Gap between "${cur.label}" (…${cur.max_plays}) and "${nxt.label}" (${nxt.min_plays}…).`);
            }
        }
        // require a top-open tier
        const hasOpenTop = activePub.some(t => t.max_plays == null);
        if (activePub.length && !hasOpenTop) {
            issues.push("No open-ended top tier — high-volume orders will fail with 'no tier applies'.");
        }
        return issues;
    }, [tiers]);

    const save = async () => {
        if (!draft) return;
        setBusy(true);
        try {
            const clean = {
                ...draft,
                min_plays: Number(draft.min_plays),
                max_plays: draft.max_plays === null || draft.max_plays === "" ? null : Number(draft.max_plays),
                price_per_play_cents: Number(draft.price_per_play_cents),
                display_order: Number(draft.display_order) || 0,
                starts_at: draft.starts_at || null,
                ends_at: draft.ends_at || null,
                restricted_user_ids: draft.restricted_user_ids || [],
            };
            const op = clean.id
                ? withElevation(() => api.patch(`/pricing/tiers/${clean.id}`, clean))
                : withElevation(() => api.post("/pricing/tiers", clean));
            await op;
            toast.success(clean.id ? "Tier updated." : "Tier created.");
            setDraft(null);
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed.");
        } finally { setBusy(false); }
    };

    const remove = async (id, label) => {
        if (!window.confirm(`Delete pricing tier "${label}"? Historical transactions are unaffected.`)) return;
        try {
            await withElevation(() => api.delete(`/pricing/tiers/${id}`));
            toast.success("Tier deleted.");
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed.");
        }
    };

    const saveMinPurchase = async () => {
        const v = Number(minEdit);
        if (!v || v < 1) { toast.error("Enter a valid minimum purchase."); return; }
        try {
            await withElevation(() => api.patch("/pricing/config", { min_purchase: v }));
            toast.success("Minimum purchase updated.");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); }
    };

    const loadHistory = async () => {
        try {
            const { data } = await api.get("/pricing/history");
            setHistory(data.history || []);
            setShowHistory(true);
        } catch (e) { toast.error("Could not load history."); }
    };

    const restore = async (eventId) => {
        if (!window.confirm("Restore this snapshot? A new history event will be recorded.")) return;
        try {
            await withElevation(() => api.post(`/pricing/history/${eventId}/restore`));
            toast.success("Tier restored.");
            setShowHistory(false);
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Restore failed."); }
    };

    if (!tiers) return <div className="text-slate-500 text-sm">Loading…</div>;

    return (
        <div className="space-y-8" data-testid="admin-pricing-tab">
            {/* Config header */}
            <section className="border border-white/10 p-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Global config</div>
                        <div className="mt-2 font-display font-black text-2xl">Minimum purchase</div>
                        <div className="text-slate-400 text-sm mt-1">Applies platform-wide. Cannot buy fewer than this many plays.</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="number"
                            min={1}
                            value={minEdit}
                            onChange={(e) => setMinEdit(e.target.value)}
                            data-testid="pricing-min-purchase-input"
                            className="w-32 bg-transparent border border-white/20 px-3 py-2 text-sm"
                        />
                        <button
                            onClick={saveMinPurchase}
                            data-testid="pricing-min-purchase-save"
                            className="text-xs font-mono-tech uppercase tracking-widest bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2 transition-colors"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </section>

            {/* Validation hints */}
            {validationHints.length > 0 && (
                <section className="border border-yellow-500/40 bg-yellow-500/5 p-5 text-yellow-100">
                    <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-yellow-400 mb-2">
                        / Tier validation warnings
                    </div>
                    <ul className="list-disc list-inside text-sm space-y-1">
                        {validationHints.map((h, i) => <li key={i}>{h}</li>)}
                    </ul>
                </section>
            )}

            {/* Tiers table */}
            <section className="border border-white/10">
                <div className="p-5 border-b border-white/10 flex items-center justify-between">
                    <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Pay-Per-Play tiers</div>
                        <div className="text-slate-400 text-sm mt-1">Create, edit, activate or restrict pricing. Historical transactions are never rewritten.</div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={loadHistory}
                            data-testid="pricing-history-btn"
                            className="text-xs font-mono-tech uppercase tracking-widest text-slate-300 hover:text-white border border-white/20 hover:border-white/40 px-3 py-2 transition-colors inline-flex items-center gap-2"
                        >
                            <History size={13} /> History
                        </button>
                        <button
                            onClick={() => setDraft(emptyTier())}
                            data-testid="pricing-add-btn"
                            className="text-xs font-mono-tech uppercase tracking-widest bg-[#FF5A00] hover:bg-[#E65200] text-white px-4 py-2 transition-colors inline-flex items-center gap-2"
                        >
                            <Plus size={13} /> Add tier
                        </button>
                    </div>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left text-[10px] font-mono-tech uppercase tracking-[0.25em] text-slate-500 border-b border-white/10">
                                <th className="p-3">Label</th>
                                <th className="p-3">Range</th>
                                <th className="p-3">Rate</th>
                                <th className="p-3">Status</th>
                                <th className="p-3">Audience</th>
                                <th className="p-3">Order</th>
                                <th className="p-3"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {tiers.map((t) => (
                                <tr key={t.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`pricing-tier-row-${t.id}`}>
                                    <td className="p-3 font-medium">{t.label}</td>
                                    <td className="p-3 text-slate-300">{t.min_plays.toLocaleString()}{t.max_plays ? `–${t.max_plays.toLocaleString()}` : "+"}</td>
                                    <td className="p-3 text-[#FF5A00]">{centsToStr(t.price_per_play_cents)}/play</td>
                                    <td className="p-3">
                                        <span className={`text-[10px] font-mono-tech uppercase tracking-[0.25em] px-2 py-1 border ${
                                            t.status === "active" ? "border-emerald-500/40 text-emerald-300" :
                                            t.status === "scheduled" ? "border-blue-500/40 text-blue-300" :
                                            t.status === "draft" ? "border-yellow-500/40 text-yellow-300" :
                                            "border-white/10 text-slate-500"
                                        }`}>{t.status}</span>
                                    </td>
                                    <td className="p-3 text-slate-300">{t.audience}{t.audience === "restricted" && ` (${(t.restricted_user_ids || []).length})`}</td>
                                    <td className="p-3 text-slate-500">{t.display_order}</td>
                                    <td className="p-3 text-right space-x-2 whitespace-nowrap">
                                        <button
                                            onClick={() => setDraft({ ...t })}
                                            data-testid={`pricing-edit-${t.id}`}
                                            className="text-xs font-mono-tech uppercase tracking-widest text-slate-300 hover:text-white"
                                        >Edit</button>
                                        <button
                                            onClick={() => remove(t.id, t.label)}
                                            data-testid={`pricing-delete-${t.id}`}
                                            className="text-xs text-red-400 hover:text-red-300"
                                        ><Trash2 size={13} className="inline" /></button>
                                    </td>
                                </tr>
                            ))}
                            {tiers.length === 0 && (
                                <tr><td colSpan={7} className="p-8 text-center text-slate-500 text-sm">No pricing tiers yet. Click &quot;Add tier&quot; to create one.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Edit modal */}
            {draft && <TierEditor draft={draft} setDraft={setDraft} save={save} busy={busy} />}
            {/* History drawer */}
            {showHistory && (
                <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-6 overflow-y-auto" onClick={() => setShowHistory(false)}>
                    <div className="max-w-3xl mx-auto bg-[#0F1E3B] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <div className="font-display font-black text-2xl">Pricing change history</div>
                            <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-white"><X size={18} /></button>
                        </div>
                        <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
                            {(history || []).map((h) => (
                                <div key={h.id} className="border border-white/10 p-3 text-sm flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-500">{h.at?.slice(0, 19).replace("T", " ")}</div>
                                        <div className="mt-1 text-white">{h.action.toUpperCase()} — {h.after?.label || h.before?.label || h.tier_id}</div>
                                        <div className="text-xs text-slate-400">By {h.actor_email}</div>
                                    </div>
                                    {(h.action === "updated" || h.action === "deleted") && h.before && (
                                        <button
                                            onClick={() => restore(h.id)}
                                            data-testid={`pricing-restore-${h.id}`}
                                            className="shrink-0 text-xs font-mono-tech uppercase tracking-widest text-[#FF5A00] hover:text-orange-300 inline-flex items-center gap-1"
                                        ><RotateCcw size={12} /> Restore</button>
                                    )}
                                </div>
                            ))}
                            {(history || []).length === 0 && <div className="text-slate-500 text-sm">No history yet.</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const TierEditor = ({ draft, setDraft, save, busy }) => {
    const upd = (k, v) => setDraft({ ...draft, [k]: v });
    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm p-6 overflow-y-auto" onClick={() => setDraft(null)}>
            <div className="max-w-2xl mx-auto bg-[#0F1E3B] border border-white/10 p-6" onClick={(e) => e.stopPropagation()} data-testid="pricing-tier-editor">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <div className="font-mono-tech text-[10px] uppercase tracking-[0.3em] text-[#FF5A00]">/ Pricing tier</div>
                        <div className="font-display font-black text-2xl">{draft.id ? "Edit tier" : "New tier"}</div>
                    </div>
                    <button onClick={() => setDraft(null)} className="text-slate-400 hover:text-white"><X size={18} /></button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Label"><input value={draft.label} onChange={(e) => upd("label", e.target.value)} data-testid="pricing-field-label" className={inputClass} /></Field>
                    <Field label="Status">
                        <select value={draft.status} onChange={(e) => upd("status", e.target.value)} data-testid="pricing-field-status" className={inputClass}>
                            {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Min plays"><input type="number" min={1} value={draft.min_plays} onChange={(e) => upd("min_plays", e.target.value)} data-testid="pricing-field-min" className={inputClass} /></Field>
                    <Field label="Max plays (blank = open)">
                        <input
                            type="number" min={1}
                            value={draft.max_plays ?? ""}
                            onChange={(e) => upd("max_plays", e.target.value === "" ? null : e.target.value)}
                            data-testid="pricing-field-max"
                            className={inputClass}
                        />
                    </Field>
                    <Field label="Price per play (cents)"><input type="number" min={1} value={draft.price_per_play_cents} onChange={(e) => upd("price_per_play_cents", e.target.value)} data-testid="pricing-field-price" className={inputClass} /></Field>
                    <Field label="Display order"><input type="number" value={draft.display_order} onChange={(e) => upd("display_order", e.target.value)} className={inputClass} /></Field>
                    <Field label="Starts at (optional)"><input type="datetime-local" value={draft.starts_at ? draft.starts_at.slice(0, 16) : ""} onChange={(e) => upd("starts_at", e.target.value ? `${e.target.value}:00Z` : null)} className={inputClass} /></Field>
                    <Field label="Ends at (optional)"><input type="datetime-local" value={draft.ends_at ? draft.ends_at.slice(0, 16) : ""} onChange={(e) => upd("ends_at", e.target.value ? `${e.target.value}:00Z` : null)} className={inputClass} /></Field>
                    <Field label="Audience">
                        <select value={draft.audience} onChange={(e) => upd("audience", e.target.value)} data-testid="pricing-field-audience" className={inputClass}>
                            <option value="public">Public</option>
                            <option value="restricted">Restricted (customer-specific)</option>
                        </select>
                    </Field>
                    {draft.audience === "restricted" && (
                        <Field label="Restricted user IDs (comma-separated)">
                            <input
                                value={(draft.restricted_user_ids || []).join(",")}
                                onChange={(e) => upd("restricted_user_ids", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
                                data-testid="pricing-field-restricted"
                                className={inputClass}
                                placeholder="user-uuid-1, user-uuid-2"
                            />
                        </Field>
                    )}
                </div>
                <div className="mt-6 flex items-center justify-end gap-2">
                    <button onClick={() => setDraft(null)} className="text-xs font-mono-tech uppercase tracking-widest text-slate-400 hover:text-white px-4 py-2">Cancel</button>
                    <button
                        onClick={save}
                        disabled={busy}
                        data-testid="pricing-save-btn"
                        className="text-xs font-mono-tech uppercase tracking-widest bg-[#FF5A00] hover:bg-[#E65200] text-white px-5 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
                    </button>
                </div>
            </div>
        </div>
    );
};

const inputClass = "w-full bg-transparent border border-white/20 focus:border-[#FF5A00] px-3 py-2 text-sm outline-none";

const Field = ({ label, children }) => (
    <div>
        <div className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5">{label}</div>
        {children}
    </div>
);
